import React, { useCallback, useState, useRef, useMemo } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { EditorView, Decoration, ViewPlugin, WidgetType } from "@codemirror/view";
import { EditorSelection } from "@codemirror/state";
import { javascript } from "@codemirror/lang-javascript";
import { autocompletion } from "@codemirror/autocomplete";
import { html } from "@codemirror/lang-html";
import { css } from "@codemirror/lang-css";
import { json } from "@codemirror/lang-json";

import BuilderModal from "../../../Layouts/Modal/BuilderModal";
import TableTreeModal from "../TableTreeModal";

const CodeEditor = React.memo(
  ({
    fieldObj,
    value,
    language = "javascript",
    height = "84vh",
    containerClass,
    tableID,
    handleChange,
    enableBacktickTrigger = true,
    readOnly
  }) => {
    const editorViewRef = useRef(null);
    const [isFieldModalOpen, setIsFieldModalOpen] = useState({
      type: "",
      valueID: "",
      startPosition: null,
      endPosition: null
    });

    const debouncedHandleChange = debounce((val) => {
      handleChange(val);
    }, 300);

    const closeFieldModal = useCallback(() => {
      setIsFieldModalOpen({
        type: "",
        valueID: "",
        startPosition: null,
        endPosition: null
      });
      //eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClickOnField = useCallback((id) => {
      const viewCurrent = editorViewRef.current;
      if (!viewCurrent) return;

      const currentDoc = viewCurrent.state.doc.toString();
      const escapedId = id.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const targetIdRegex = new RegExp(`#${escapedId}#`, "g");
      let match;
      let foundFrom = -1;
      let foundTo = -1;

      while ((match = targetIdRegex.exec(currentDoc)) !== null) {
        foundFrom = match.index;
        foundTo = foundFrom + match[0].length;
        break;
      }

      if (foundFrom !== -1) {
        setIsFieldModalOpen({
          type: "edit",
          valueID: id,
          startPosition: foundFrom,
          endPosition: foundTo
        });
      } else {
        console.warn(
          `Could not find original field '#${id}#' in document for editing. This might occur if the text was manually altered or the field ID is incorrect.`
        );
      }
      //eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTreeModalCallback = useCallback(
      (data) => {
        const newFieldId = data.id;
        const view = editorViewRef.current;
        if (!view) {
          console.warn("CodeMirror editor view not available.");
          return;
        }

        view.dispatch(
          view.state.changeByRange((range) => {
            let changes = [];
            let newCursorPos = range.head;

            if (isFieldModalOpen.type === "add" && isFieldModalOpen.startPosition !== null) {
              const from = isFieldModalOpen.startPosition;
              const to = isFieldModalOpen.endPosition;
              const replacement = `#${newFieldId}#`;
              changes.push({ from, to, insert: replacement });
              newCursorPos = from + replacement.length;
            } else if (isFieldModalOpen.type === "edit" && isFieldModalOpen.startPosition !== null) {
              const oldFieldId = isFieldModalOpen.valueID;
              const start = isFieldModalOpen.startPosition;
              const end = isFieldModalOpen.endPosition;

              if (newFieldId === oldFieldId) {
                closeFieldModal();
                return { changes: [], range: EditorSelection.range(range.anchor, range.head) };
              }

              const replacement = `#${newFieldId}#`;
              changes.push({ from: start, to: end, insert: replacement });
              newCursorPos = start + replacement.length;
            } else {
              const replacement = `#${newFieldId}#`;
              changes.push({ from: range.from, to: range.to, insert: replacement });
              newCursorPos = range.from + replacement.length;
            }

            return { changes, range: EditorSelection.cursor(newCursorPos) };
          })
        );

        closeFieldModal();
      },

      //eslint-disable-next-line react-hooks/exhaustive-deps
      [isFieldModalOpen]
    );

    const handleEditorChange = useCallback(
      (valueChange) => {
        debouncedHandleChange(valueChange);
      },

      //eslint-disable-next-line react-hooks/exhaustive-deps
      [debouncedHandleChange]
    );

    const handleEditorDidMount = useCallback((view) => {
      editorViewRef.current = view;
    }, []);

    const extensions = useMemo(() => {
      return [
        getLanguageExtension(language),
        createFieldDecorations(fieldObj, handleClickOnField),
        autocompletion({
          override: [createFieldCompletionSource(fieldObj)]
        }),
        backtickDetectionExtension(setIsFieldModalOpen, !!isFieldModalOpen.type, enableBacktickTrigger)
      ];

      //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language, fieldObj, isFieldModalOpen.type]);

    return (
      <>
        <BuilderModal isOpen={!!isFieldModalOpen.type} onClose={closeFieldModal} modalName="Select Field">
          <TableTreeModal callbackFunction={handleTreeModalCallback} tableId={tableID} />
        </BuilderModal>

        <div
          style={{ background: "#1E1E1E", height: height, borderRadius: "8px", overflow: "hidden" }}
          className={containerClass}
        >
          <CodeMirror
            value={value}
            height={height}
            theme="dark"
            editable={!readOnly}
            basicSetup={{
              lineNumbers: true,
              foldGutter: true,
              highlightActiveLineGutter: true,
              highlightActiveLine: true
            }}
            extensions={extensions}
            onChange={readOnly ? undefined : handleEditorChange}
            onCreateEditor={handleEditorDidMount}
          />
        </div>
      </>
    );
  }
);

export default CodeEditor;

const debounce = (func, delay) => {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), delay);
  };
};

const createFieldDecorations = (fields, onClickField) =>
  ViewPlugin.fromClass(
    class {
      constructor(view) {
        this.decorations = this.buildDecorations(view, fields, onClickField);
      }

      update(update) {
        if (update.docChanged || update.viewportChanged) {
          this.decorations = this.buildDecorations(update.view, fields, onClickField);
        }
      }

      buildDecorations(view, fields, onClickField) {
        const widgets = [];
        const text = view.state.doc.toString();
        const regex = /#([\w.-]+)#/g;
        let match;

        while ((match = regex.exec(text)) !== null) {
          const id = match[1];
          const label = fields[id]?.display;

          if (!label) continue;

          const from = match.index;
          const to = from + match[0].length;

          const deco = Decoration.replace({
            widget: new FieldWidget(label, id, onClickField),
            inclusive: false,
            block: false
          }).range(from, to);

          widgets.push(deco);
        }

        return Decoration.set(widgets);
      }
    },
    {
      decorations: (v) => v.decorations
    }
  );

const createFieldCompletionSource = (fieldsDefinition) => (context) => {
  let word = context.matchBefore(/#[\w.-]*$/);
  if (!word && !context.explicit) return null;

  const from = word ? word.from : context.pos;

  const options = Object.keys(fieldsDefinition).map((key) => ({
    label: `#${key}#`,
    type: "variable",
    detail: fieldsDefinition[key].display,
    apply: `#${key}#`
  }));

  return {
    from,
    options,
    filter: (completion, state) => {
      const currentText = state.sliceDoc(from, context.pos);
      const filterText = currentText.startsWith("#") ? currentText.substring(1) : currentText;
      return completion.label.toLowerCase().includes(filterText.toLowerCase());
    }
  };
};

const openFieldModalCommand = (view, setIsFieldModalOpen, enableBacktickTrigger) => {
  const currentDoc = view.state.doc.toString();
  const backticksRegex = /```/;
  const match = backticksRegex.exec(currentDoc);

  if (match && enableBacktickTrigger === true) {
    if (view.dom.closest("[data-modal-open='true']")) {
      return false;
    }

    setIsFieldModalOpen((prev) => {
      if (!prev.type) {
        return {
          type: "add",
          valueID: "",
          startPosition: match.index,
          endPosition: match.index + match[0].length
        };
      }
      return prev;
    });
    return true;
  }
  return false;
};

let modalTriggeredInternally = false;

const backtickDetectionExtension = (setIsFieldModalOpen, isModalCurrentlyOpen, enableBacktickTrigger) => {
  return EditorView.updateListener.of((update) => {
    if (!update.docChanged) {
      return;
    }

    const currentDoc = update.view.state.doc.toString();
    const backticksRegex = /```/;
    const match = backticksRegex.exec(currentDoc);

    if (
      match &&
      !isModalCurrentlyOpen &&
      !update.view.dom.closest("[data-modal-open='true']") &&
      !modalTriggeredInternally
    ) {
      const innerMatch = backticksRegex.exec(update.view.state.doc.toString());
      if (innerMatch && !isModalCurrentlyOpen && !update.view.dom.closest("[data-modal-open='true']")) {
        openFieldModalCommand(update.view, setIsFieldModalOpen, enableBacktickTrigger);
        modalTriggeredInternally = true;
      }
    } else if (!match && modalTriggeredInternally) {
      modalTriggeredInternally = false;
    }
  });
};

const languageExtensions = {
  javascript: javascript(),
  html: html(),
  css: css(),
  json: json()
};

const getLanguageExtension = (lang) => {
  return languageExtensions[lang] || javascript();
};

class FieldWidget extends WidgetType {
  constructor(label, id, onClickField) {
    super();
    this.label = label;
    this.id = id;
    this.onClickField = onClickField;
  }

  eq(other) {
    return other.label === this.label && other.id === this.id;
  }

  toDOM() {
    const span = document.createElement("span");
    span.textContent = this.label;
    span.style.background = "#444";
    span.style.color = "white";
    span.style.padding = "2px 6px";
    span.style.borderRadius = "4px";
    span.style.cursor = "pointer";
    span.style.whiteSpace = "nowrap";
    span.style.fontFamily = "Inter, sans-serif";
    span.style.fontSize = "0.875rem";

    span.onclick = (e) => {
      e.stopPropagation();
      this.onClickField?.(this.id, this.label);
    };
    return span;
  }

  updateDOM() {
    return false;
  }
}
